/**
 * Internal linking utility for SEO maillage interne.
 * Automatically injects <a> links into blog article HTML content
 * when matching keywords are found.
 */

interface LinkRule {
  /** Keywords/phrases to match (case-insensitive). First match per article only. */
  keywords: string[];
  /** Target URL */
  url: string;
  /** Optional link text override (otherwise uses matched keyword) */
  label?: string;
}

// --- Link rules: article → article ---
const articleLinks: LinkRule[] = [
  {
    keywords: ['épilation laser peau noire', 'laser peau noire', 'épilation laser sur peau noire'],
    url: '/blog/epilation-laser-peau-noire-sevran',
  },
  {
    keywords: ['épilation laser ou cire', 'laser ou cire', 'cire ou laser'],
    url: '/blog/epilation-laser-vs-cire-comparatif',
  },
  {
    keywords: ['électrolyse', 'épilation électrolyse', 'épilation définitive'],
    url: '/blog/epilation-electrolyse-poils-incarnes',
  },
  {
    keywords: ['épilation laser maillot', 'maillot intégral', 'maillot brésilien'],
    url: '/blog/epilation-laser-maillot-bresilien-sevran',
  },
  {
    keywords: ['épilation cire visage', 'cire visage', 'épilation sourcils'],
    url: '/blog/epilation-cire-visage-levres-menton',
  },
  {
    keywords: ['soin hydratation éclat', 'soin hydratation et éclat', 'teint terne'],
    url: '/blog/soin-visage-hydratation-eclat',
  },
  {
    keywords: ['soin anti-âge', 'anti-âge raffermissement', 'perte de fermeté'],
    url: '/blog/soin-visage-anti-age-raffermissement',
  },
  {
    keywords: ['peeling et LED', 'peeling chimique', 'photothérapie LED'],
    url: '/blog/peeling-phototherapie-led-sevran',
  },
  {
    keywords: ['Skincleantech', 'nettoyage de peau profond'],
    url: '/blog/skincleantech-soin-visage-profond-sevran',
  },
  {
    keywords: ['HIFU lifting', 'lifting sans chirurgie', 'ultrasons focalisés'],
    url: '/blog/hifu-lifting-sans-chirurgie-sevran',
  },
  {
    keywords: ['pressothérapie', 'drainage lymphatique', 'jambes lourdes'],
    url: '/blog/pressotherapie-jambes-legeres-drainage',
  },
  {
    keywords: ['lipolaser', 'graisses rebelles', 'lipolyse laser'],
    url: '/blog/lipolaser-elimination-graisses-corps',
  },
  {
    keywords: ['soins corps minceur', 'remodelage corporel'],
    url: '/blog/soins-corps-minceur-remodelage-sevran',
  },
  {
    keywords: ['soins post-partum', 'après la grossesse', 'post-grossesse'],
    url: '/blog/soin-corps-grossesse-post-partum',
  },
  {
    keywords: ['vernis semi-permanent', 'semi-permanent'],
    url: '/blog/vernis-semi-permanent-tenue-duree',
  },
  {
    keywords: ['pose gel', 'capsules ongles', 'faux ongles'],
    url: '/blog/pose-gel-capsules-ongles-sevran',
  },
  {
    keywords: ['pédicure complète', 'soins des pieds', 'beauté des pieds'],
    url: '/blog/pedicure-soins-pieds-complet',
  },
  {
    keywords: ['routine beauté peau noire', 'routine peau noire', 'peau noire et métissée'],
    url: '/blog/routine-beaute-peau-noire-metissee',
  },
  {
    keywords: ['taches hyperpigmentation', 'hyperpigmentation peau noire', 'masque de grossesse'],
    url: '/blog/traitement-taches-hyperpigmentation-peau-noire',
  },
  {
    keywords: ['acné adulte', 'acné femme', 'peau acnéique'],
    url: '/blog/soin-visage-acne-adulte-femme',
  },
  {
    keywords: ['tendances beauté 2025', 'tendances esthétiques'],
    url: '/blog/tendances-beaute-soins-esthetiques-2025',
  },
  {
    keywords: ['préparer sa peau pour l\'été', 'peau été'],
    url: '/blog/preparer-peau-ete-soleil-soins',
  },
  {
    keywords: ['carte cadeau', 'offrir un soin'],
    url: '/blog/carte-cadeau-soin-institut-offrir',
  },
];

// --- Link rules: article → pages ---
const pageLinks: LinkRule[] = [
  {
    keywords: ['soins du visage', 'soins visage'],
    url: '/services/soins-visage',
  },
  {
    keywords: ['soins du corps', 'soins corps'],
    url: '/services/soins-corps',
  },
  {
    keywords: ['HIFU visage', 'HIFU corps'],
    url: '/services/hifu',
  },
  {
    keywords: ['blanchiment dentaire'],
    url: '/services/blanchiment-dentaire',
  },
  {
    keywords: ['manucure', 'beauté des mains'],
    url: '/services/manucure',
  },
  {
    keywords: ['beauté des pieds', 'pédicure'],
    url: '/services/beaute-des-pieds',
  },
  {
    keywords: ['épilation cire', 'épilation fil'],
    url: '/services/epilation',
  },
  {
    keywords: ['spray tan', 'BOTAN'],
    url: '/services/spray-tan',
  },
  {
    keywords: ['massages bien-être', 'massage relaxant'],
    url: '/services/massages',
  },
  {
    keywords: ['formules avantageuses', 'nos formules'],
    url: '/formules',
  },
  {
    keywords: ['carte cadeau Body by CC'],
    url: '/offrir',
  },
];

const allLinks = [...articleLinks, ...pageLinks];

/**
 * Process article HTML and inject internal links.
 * - Only injects ONE link per rule (first match).
 * - Does NOT inject links inside existing <a>, <h2>, <h3>, <strong>, <blockquote> tags.
 * - Skips self-referencing links (won't link to the current article).
 */
export function injectInternalLinks(html: string, currentSlug: string): string {
  const currentUrl = `/blog/${currentSlug}`;
  // Track which URLs we've already linked to avoid duplicates
  const linkedUrls = new Set<string>();

  // Collect existing links in the HTML to avoid duplicating
  const existingLinkRegex = /href="([^"]+)"/g;
  let existingMatch;
  while ((existingMatch = existingLinkRegex.exec(html)) !== null) {
    linkedUrls.add(existingMatch[1]);
  }

  let result = html;

  for (const rule of allLinks) {
    // Skip if this is a self-link or already linked
    if (rule.url === currentUrl || linkedUrls.has(rule.url)) continue;

    for (const keyword of rule.keywords) {
      // Build regex that matches the keyword NOT inside HTML tags
      // We match only in text content (not inside tag attributes or existing links)
      const escapedKeyword = keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(
        `(?<=>)([^<]*?)\\b(${escapedKeyword})\\b([^<]*?)(?=<)`,
        'i'
      );

      const match = regex.exec(result);
      if (match) {
        const fullMatch = match[0];
        const before = match[1];
        const matchedText = match[2];
        const after = match[3];

        // Check we're not inside an <a>, <h2>, <h3> tag by looking backwards
        const matchIndex = match.index;
        const precedingHtml = result.substring(Math.max(0, matchIndex - 200), matchIndex);

        // Skip if inside an anchor, heading or strong tag
        const lastOpenTag = precedingHtml.match(/<(a|h[23]|strong|blockquote)[^>]*>[^<]*$/i);
        if (lastOpenTag) continue;

        const linkHtml = `${before}<a href="${rule.url}" class="text-primary hover:underline">${matchedText}</a>${after}`;
        result = result.substring(0, match.index) + linkHtml + result.substring(match.index + fullMatch.length);
        linkedUrls.add(rule.url);
        break; // Only one link per rule
      }
    }
  }

  return result;
}
